import React, { memo } from 'react';

/**
 * Decorative grass strip rendered at the bottom of the arena.
 * Uses an inline SVG so no external assets are required.
 */
function GrassStripComponent(): React.ReactElement {
  return (
    <div className="relative w-full overflow-hidden" style={{ height: 72, flexShrink: 0 }}>
      <svg
        viewBox="0 0 820 72"
        preserveAspectRatio="xMidYMax slice"
        className="absolute bottom-0 left-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Base grass bed */}
        <rect x="0" y="40" width="820" height="32" fill="#1a4a0a" />

        {/* Grass blades — varied heights and positions for a natural look */}
        {GRASS_BLADES.map((blade, i) => (
          <path
            key={i}
            d={blade.d}
            fill={blade.dark ? '#1d5c0c' : '#24760e'}
            opacity={blade.opacity}
          />
        ))}

        {/* Highlight layer */}
        {GRASS_BLADES.filter((_, i) => i % 3 === 0).map((blade, i) => (
          <path
            key={`h-${i}`}
            d={blade.d}
            fill="#2e9410"
            opacity={0.35}
          />
        ))}

        {/* Dark ground line */}
        <rect x="0" y="40" width="820" height="3" fill="#0e3206" />
      </svg>
    </div>
  );
}

export const GrassStrip = memo(GrassStripComponent);

// ─── Grass blade data ──────────────────────────────────────────────────────────

interface GrassBlade {
  d: string;
  dark: boolean;
  opacity: number;
}

/** Pre-computed grass blade SVG paths for a natural-looking strip */
const GRASS_BLADES: GrassBlade[] = [
  // Format: M x,40 Q cx,cy ex,ey — quadratic bezier blades
  { d: 'M0,40 Q4,18 8,40 Z', dark: false, opacity: 0.9 },
  { d: 'M5,40 Q10,10 14,40 Z', dark: true, opacity: 0.85 },
  { d: 'M12,40 Q15,22 20,38 Z', dark: false, opacity: 0.9 },
  { d: 'M18,40 Q24,6 28,40 Z', dark: false, opacity: 0.95 },
  { d: 'M26,40 Q30,15 35,39 Z', dark: true, opacity: 0.8 },
  { d: 'M33,40 Q37,8 42,40 Z', dark: false, opacity: 0.9 },
  { d: 'M40,40 Q45,20 50,38 Z', dark: false, opacity: 0.85 },
  { d: 'M48,40 Q51,12 56,40 Z', dark: true, opacity: 0.9 },
  { d: 'M55,40 Q60,5 65,40 Z', dark: false, opacity: 0.95 },
  { d: 'M63,40 Q67,18 72,39 Z', dark: false, opacity: 0.8 },
  { d: 'M70,40 Q75,11 80,40 Z', dark: true, opacity: 0.9 },
  { d: 'M78,40 Q83,22 88,38 Z', dark: false, opacity: 0.85 },
  { d: 'M86,40 Q90,7 95,40 Z', dark: false, opacity: 0.9 },
  { d: 'M93,40 Q98,16 103,40 Z', dark: true, opacity: 0.8 },
  { d: 'M101,40 Q105,9 110,40 Z', dark: false, opacity: 0.95 },
  { d: 'M109,40 Q113,20 118,39 Z', dark: false, opacity: 0.85 },
  { d: 'M116,40 Q121,13 126,40 Z', dark: true, opacity: 0.9 },
  { d: 'M124,40 Q128,6 133,40 Z', dark: false, opacity: 0.9 },
  { d: 'M132,40 Q137,18 142,38 Z', dark: false, opacity: 0.8 },
  { d: 'M140,40 Q144,10 149,40 Z', dark: true, opacity: 0.85 },
  { d: 'M148,40 Q152,23 157,40 Z', dark: false, opacity: 0.9 },
  { d: 'M155,40 Q160,7 165,40 Z', dark: false, opacity: 0.95 },
  { d: 'M163,40 Q167,15 172,39 Z', dark: true, opacity: 0.8 },
  { d: 'M171,40 Q176,11 181,40 Z', dark: false, opacity: 0.9 },
  { d: 'M179,40 Q183,21 188,38 Z', dark: false, opacity: 0.85 },
  { d: 'M187,40 Q191,8 196,40 Z', dark: true, opacity: 0.9 },
  { d: 'M195,40 Q200,4 205,40 Z', dark: false, opacity: 0.95 },
  { d: 'M203,40 Q207,17 212,40 Z', dark: false, opacity: 0.8 },
  { d: 'M211,40 Q215,12 220,39 Z', dark: true, opacity: 0.85 },
  { d: 'M219,40 Q224,6 229,40 Z', dark: false, opacity: 0.9 },
  { d: 'M227,40 Q231,19 236,38 Z', dark: false, opacity: 0.9 },
  { d: 'M235,40 Q240,9 245,40 Z', dark: true, opacity: 0.8 },
  { d: 'M243,40 Q247,22 252,40 Z', dark: false, opacity: 0.85 },
  { d: 'M251,40 Q256,5 261,40 Z', dark: false, opacity: 0.95 },
  { d: 'M259,40 Q263,14 268,39 Z', dark: true, opacity: 0.9 },
  { d: 'M267,40 Q272,10 277,40 Z', dark: false, opacity: 0.9 },
  { d: 'M275,40 Q279,20 284,38 Z', dark: false, opacity: 0.8 },
  { d: 'M283,40 Q288,7 293,40 Z', dark: true, opacity: 0.85 },
  { d: 'M291,40 Q295,16 300,40 Z', dark: false, opacity: 0.9 },
  { d: 'M299,40 Q304,11 309,40 Z', dark: false, opacity: 0.95 },
  { d: 'M307,40 Q311,23 316,39 Z', dark: true, opacity: 0.8 },
  { d: 'M315,40 Q320,6 325,40 Z', dark: false, opacity: 0.9 },
  { d: 'M323,40 Q327,18 332,38 Z', dark: false, opacity: 0.85 },
  { d: 'M331,40 Q336,9 341,40 Z', dark: true, opacity: 0.9 },
  { d: 'M339,40 Q343,21 348,40 Z', dark: false, opacity: 0.9 },
  { d: 'M347,40 Q352,5 357,40 Z', dark: false, opacity: 0.8 },
  { d: 'M355,40 Q359,13 364,39 Z', dark: true, opacity: 0.85 },
  { d: 'M363,40 Q368,8 373,40 Z', dark: false, opacity: 0.95 },
  { d: 'M371,40 Q375,20 380,38 Z', dark: false, opacity: 0.9 },
  { d: 'M379,40 Q384,11 389,40 Z', dark: true, opacity: 0.8 },
  { d: 'M387,40 Q391,17 396,40 Z', dark: false, opacity: 0.85 },
  { d: 'M395,40 Q400,4 405,40 Z', dark: false, opacity: 0.9 },
  { d: 'M403,40 Q407,22 412,39 Z', dark: true, opacity: 0.95 },
  { d: 'M411,40 Q416,10 421,40 Z', dark: false, opacity: 0.9 },
  { d: 'M419,40 Q423,15 428,38 Z', dark: false, opacity: 0.8 },
  { d: 'M427,40 Q432,7 437,40 Z', dark: true, opacity: 0.85 },
  { d: 'M435,40 Q439,19 444,40 Z', dark: false, opacity: 0.9 },
  { d: 'M443,40 Q448,6 453,40 Z', dark: false, opacity: 0.95 },
  { d: 'M451,40 Q455,21 460,39 Z', dark: true, opacity: 0.8 },
  { d: 'M459,40 Q464,12 469,40 Z', dark: false, opacity: 0.9 },
  { d: 'M467,40 Q471,8 476,38 Z', dark: false, opacity: 0.85 },
  { d: 'M475,40 Q480,18 485,40 Z', dark: true, opacity: 0.9 },
  { d: 'M483,40 Q487,5 492,40 Z', dark: false, opacity: 0.9 },
  { d: 'M491,40 Q496,14 501,39 Z', dark: false, opacity: 0.8 },
  { d: 'M499,40 Q503,9 508,40 Z', dark: true, opacity: 0.85 },
  { d: 'M507,40 Q512,22 517,38 Z', dark: false, opacity: 0.95 },
  { d: 'M515,40 Q519,7 524,40 Z', dark: false, opacity: 0.9 },
  { d: 'M523,40 Q528,16 533,40 Z', dark: true, opacity: 0.8 },
  { d: 'M531,40 Q535,11 540,39 Z', dark: false, opacity: 0.85 },
  { d: 'M539,40 Q544,20 549,40 Z', dark: false, opacity: 0.9 },
  { d: 'M547,40 Q551,6 556,40 Z', dark: true, opacity: 0.95 },
  { d: 'M555,40 Q560,17 565,38 Z', dark: false, opacity: 0.9 },
  { d: 'M563,40 Q567,10 572,40 Z', dark: false, opacity: 0.8 },
  { d: 'M571,40 Q576,23 581,40 Z', dark: true, opacity: 0.85 },
  { d: 'M579,40 Q583,8 588,39 Z', dark: false, opacity: 0.9 },
  { d: 'M587,40 Q592,13 597,40 Z', dark: false, opacity: 0.95 },
  { d: 'M595,40 Q599,19 604,38 Z', dark: true, opacity: 0.8 },
  { d: 'M603,40 Q608,5 613,40 Z', dark: false, opacity: 0.9 },
  { d: 'M611,40 Q615,21 620,40 Z', dark: false, opacity: 0.85 },
  { d: 'M619,40 Q624,9 629,39 Z', dark: true, opacity: 0.9 },
  { d: 'M627,40 Q631,15 636,40 Z', dark: false, opacity: 0.9 },
  { d: 'M635,40 Q640,7 645,40 Z', dark: false, opacity: 0.8 },
  { d: 'M643,40 Q647,20 652,38 Z', dark: true, opacity: 0.85 },
  { d: 'M651,40 Q656,11 661,40 Z', dark: false, opacity: 0.95 },
  { d: 'M659,40 Q663,17 668,39 Z', dark: false, opacity: 0.9 },
  { d: 'M667,40 Q672,6 677,40 Z', dark: true, opacity: 0.8 },
  { d: 'M675,40 Q679,22 684,40 Z', dark: false, opacity: 0.85 },
  { d: 'M683,40 Q688,10 693,40 Z', dark: false, opacity: 0.9 },
  { d: 'M691,40 Q695,14 700,39 Z', dark: true, opacity: 0.95 },
  { d: 'M699,40 Q704,8 709,40 Z', dark: false, opacity: 0.9 },
  { d: 'M707,40 Q711,19 716,38 Z', dark: false, opacity: 0.8 },
  { d: 'M715,40 Q720,5 725,40 Z', dark: true, opacity: 0.85 },
  { d: 'M723,40 Q727,13 732,40 Z', dark: false, opacity: 0.9 },
  { d: 'M731,40 Q736,21 741,39 Z', dark: false, opacity: 0.95 },
  { d: 'M739,40 Q743,9 748,40 Z', dark: true, opacity: 0.8 },
  { d: 'M747,40 Q752,16 757,40 Z', dark: false, opacity: 0.9 },
  { d: 'M755,40 Q759,7 764,38 Z', dark: false, opacity: 0.85 },
  { d: 'M763,40 Q768,20 773,40 Z', dark: true, opacity: 0.9 },
  { d: 'M771,40 Q775,11 780,39 Z', dark: false, opacity: 0.9 },
  { d: 'M779,40 Q784,6 789,40 Z', dark: false, opacity: 0.8 },
  { d: 'M787,40 Q791,18 796,40 Z', dark: true, opacity: 0.85 },
  { d: 'M795,40 Q800,23 805,39 Z', dark: false, opacity: 0.95 },
  { d: 'M803,40 Q807,10 812,40 Z', dark: false, opacity: 0.9 },
  { d: 'M811,40 Q815,15 820,40 Z', dark: true, opacity: 0.8 },
];
